import { ChangeEvent, ReactNode, useMemo } from 'react'
import clsx from 'clsx'

import { useRadioGroupContext } from './context'

import styles from './RadioItem.module.scss'

export interface RadioItemProps
  extends React.LabelHTMLAttributes<HTMLLabelElement> {
  value: string
  children: ReactNode
  disabled?: boolean
}

const RadioItem = ({
  value,
  children,
  disabled = false,
  className,
  ...rest
}: RadioItemProps) => {
  const context = useRadioGroupContext()

  if (!context) {
    throw new Error('RadioItem must be used within a <RadioGroup> component.')
  }

  const { name, onChange, selectedValue, hasRadioMark, variant } = context

  const isSelected = useMemo(
    () => selectedValue === value,
    [selectedValue, value]
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onChange(value, e)
    }
  }

  const isBoxed = variant === 'boxed'

  return (
    <label
      {...rest}
      className={clsx(
        styles.radioItem,
        isBoxed ? styles.boxed : styles.unboxed,
        {
          [styles.boxedSelected]: isBoxed && isSelected,
          [styles.boxedAccent]: isBoxed && isSelected && !hasRadioMark,
          [styles.disabled]: disabled
        },
        className
      )}
    >
      <input
        type='radio'
        name={name}
        value={value}
        checked={isSelected}
        disabled={disabled}
        onChange={handleChange}
        className={clsx(
          styles.input,
          hasRadioMark ? styles.inputWithMark : styles.inputWithoutMark
        )}
      />
      {children}
    </label>
  )
}

export default RadioItem
